import { useState, useMemo } from "react";
import { ToolsShell, TOOLS_ACCENT, ResultCard, Stat } from "./ToolsShell.jsx";

// Fraction Calculator — mixed-number arithmetic. Separate inputs for
// whole, numerator, denominator (rather than one text field) so the
// mobile keyboard stays numeric and the user doesn't have to type "/".
//
// All math in exact rational form via {num, den} integers; only the
// final display converts to decimal.

const gcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };
const reduce = (n, d) => { const sign = (n < 0) !== (d < 0) ? -1 : 1; const a = Math.abs(n), b = Math.abs(d); const g = gcd(a, b); return { num: sign * (a / g), den: b / g }; };

// Build {num, den} from whole, num, den input strings.
const toFrac = (whole, num, den) => {
  const w = parseInt(whole, 10) || 0;
  const n = parseInt(num, 10) || 0;
  const d = parseInt(den, 10);
  if (isNaN(d) || d === 0) {
    // No fractional part; treat as integer
    return { num: w, den: 1 };
  }
  // For negative whole numbers, treat n/d as also negative-direction.
  const sign = w < 0 ? -1 : 1;
  const top = Math.abs(w) * d + n;
  return reduce(sign * top, d);
};

const fracToMixed = (f) => {
  if (f.den === 1) return { whole: f.num, num: 0, den: 1 };
  const whole = Math.trunc(f.num / f.den);
  const remainder = f.num - whole * f.den;
  return { whole, num: Math.abs(remainder), den: f.den };
};

const fmtMixed = (f) => {
  const m = fracToMixed(f);
  if (m.num === 0) return String(m.whole);
  if (m.whole === 0) return `${m.num}/${m.den}`;
  return `${m.whole} ${m.num}/${m.den}`;
};

const fmtImproper = (f) => `${f.num}/${f.den}`;
const fmtDecimal = (f) => (f.num / f.den).toFixed(4).replace(/\.?0+$/, "");

const OPS = [
  { id: "+", label: "+" },
  { id: "-", label: "−" },
  { id: "×", label: "×" },
  { id: "÷", label: "÷" },
];

const compute = (a, op, b) => {
  if (op === "+") return reduce(a.num * b.den + b.num * a.den, a.den * b.den);
  if (op === "-") return reduce(a.num * b.den - b.num * a.den, a.den * b.den);
  if (op === "×") return reduce(a.num * b.num, a.den * b.den);
  if (op === "÷") { if (b.num === 0) return null; return reduce(a.num * b.den, a.den * b.num); }
  return null;
};

export default function FractionCalc() {
  const [aw, setAw] = useState("1"); const [an, setAn] = useState("1"); const [ad, setAd] = useState("2");
  const [bw, setBw] = useState("2"); const [bn, setBn] = useState("1"); const [bd, setBd] = useState("4");
  const [op, setOp] = useState("+");

  const result = useMemo(() => {
    const a = toFrac(aw, an, ad);
    const b = toFrac(bw, bn, bd);
    const r = compute(a, op, b);
    if (r == null) return { err: "Division by zero." };
    return { a, b, r };
  }, [aw, an, ad, bw, bn, bd, op]);

  return (
    <ToolsShell title="Fraction Calculator">
      <p style={{ fontSize: 14, color: "#C8CADF", marginTop: 0 }}>
        Add, subtract, multiply, divide mixed numbers. Result shown as improper fraction, mixed number, and decimal.
      </p>

      <FractionInput label="First number" whole={aw} num={an} den={ad} setWhole={setAw} setNum={setAn} setDen={setAd} />

      <div style={{ display: "flex", gap: 6, margin: "16px 0", justifyContent: "center" }}>
        {OPS.map((o) => (
          <button
            key={o.id}
            onClick={() => setOp(o.id)}
            style={{ background: op === o.id ? TOOLS_ACCENT : "#1C1E2E", color: op === o.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 18, fontWeight: 700, cursor: "pointer", minWidth: 50 }}
          >
            {o.label}
          </button>
        ))}
      </div>

      <FractionInput label="Second number" whole={bw} num={bn} den={bd} setWhole={setBw} setNum={setBn} setDen={setBd} />

      <ResultCard error={!!result.err}>
        {result.err ? (
          <div style={{ color: "#F8716A", fontSize: 14 }}>{result.err}</div>
        ) : (
          <>
            <Stat label="Result" value={fmtMixed(result.r)} big tone="accent" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
              <div style={{ background: "#0F1018", borderRadius: 8, padding: "10px 12px" }}>
                <Stat label="Improper" value={fmtImproper(result.r)} />
              </div>
              <div style={{ background: "#0F1018", borderRadius: 8, padding: "10px 12px" }}>
                <Stat label="Decimal" value={fmtDecimal(result.r)} />
              </div>
            </div>
          </>
        )}
      </ResultCard>
    </ToolsShell>
  );
}

function FractionInput({ label, whole, num, den, setWhole, setNum, setDen }) {
  const inputStyle = {
    width: "100%", background: "#13141F", border: "1px solid #262839",
    borderRadius: 8, color: "#E8E9F3", fontSize: 18, padding: "10px 6px",
    boxSizing: "border-box", textAlign: "center", fontWeight: 600,
  };
  return (
    <div>
      <div style={{ fontSize: 13, color: "#9A9DBE", marginBottom: 8 }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 10, color: "#5B5E7A", textAlign: "center", marginBottom: 2 }}>Whole</div>
          <input value={whole} onChange={(e) => setWhole(e.target.value.replace(/[^\d-]/g, ""))} inputMode="numeric" style={inputStyle} />
        </div>
        <div style={{ paddingTop: 14 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <input value={num} onChange={(e) => setNum(e.target.value.replace(/[^\d]/g, ""))} inputMode="numeric" style={{ ...inputStyle, width: 56, padding: "6px 0" }} placeholder="num" />
            <div style={{ height: 2, background: "#7C8099", width: 56 }} />
            <input value={den} onChange={(e) => setDen(e.target.value.replace(/[^\d]/g, ""))} inputMode="numeric" style={{ ...inputStyle, width: 56, padding: "6px 0" }} placeholder="den" />
          </div>
        </div>
        <div /> {/* spacer to right-align the fraction input */}
      </div>
    </div>
  );
}

